import { alpha } from '@mui/material/styles';
import { THEME_COLORS } from '../shared/constants/theme';
import {
  DASHBOARD_ACCENT,
  getDashboardDarkSurfaceTokens,
  getDashboardPageShellSx,
  getDashboardPaperSx,
  getDashboardSectionContentPaperSx,
  getDashboardSectionHeaderDividerSx,
} from './dashboardSurfaceSx';

export const DYNAMIC_PRICING_ACCENT = DASHBOARD_ACCENT;
export const DYNAMIC_PRICING_ACCENT_HOVER = THEME_COLORS.PRIMARY_HOVER;
export const DYNAMIC_PRICING_ACCENT_TEXT_LIGHT = '#0d3d32';

export { getDashboardPageShellSx };

/** Alias for stock-style theme tokens (text, surface, borders). */
export function getDynamicPricingThemeColors(isDark) {
  return getDashboardDarkSurfaceTokens(isDark);
}

/** @deprecated Use getDynamicPricingThemeColors */
export function getDynamicPricingPageColors(isDark) {
  const tokens = getDynamicPricingThemeColors(isDark);
  return {
    pageBg: tokens.background,
    paperBg: tokens.surface,
    divider: tokens.surfaceBorder,
    drawerBg: tokens.headerBg,
    surfaceDark: THEME_COLORS.BACKGROUND_DARK,
    surfaceElevated: THEME_COLORS.SURFACE_ELEVATED_DARK,
  };
}

/** Tabs strip — same pattern as Stock (`getStockTabsPaperSx`). */
export function getDynamicPricingTabsPaperSx(isDark, isTablet = false) {
  const tabPadding = isTablet ? { xs: 1, sm: 1.25 } : { xs: 1, sm: 1.5 };
  return {
    mb: isTablet ? 2 : 3,
    overflow: 'hidden',
    p: tabPadding,
    ...getDashboardSectionContentPaperSx(isDark, false, { p: tabPadding, minHeight: 0 }),
  };
}

/** Tab panel wrapper below NotchTabs. */
export function getDynamicPricingTabPanelSx(isDark, isTablet = false) {
  return {
    ...getDashboardSectionContentPaperSx(isDark, false, {
      p: isTablet ? { xs: 2, sm: 2.25 } : { xs: 2, sm: 3 },
      minHeight: 0,
    }),
    mb: 0,
  };
}

function chartCardBase(isDark, themeColors, accent, options = {}) {
  const { p = { xs: 2.2, md: 2.5 } } = options;
  return {
    p,
    background: isDark
      ? `linear-gradient(180deg, ${alpha(accent, 0.08)} 0%, ${THEME_COLORS.SURFACE_ELEVATED_DARK} 34%, ${THEME_COLORS.BACKGROUND_DARK} 100%)`
      : `linear-gradient(180deg, ${alpha(accent, 0.08)} 0%, rgba(255,255,255,0.99) 28%, rgba(248,250,252,0.99) 100%)`,
    border: `1px solid ${alpha(accent, isDark ? 0.24 : 0.18)}`,
    borderRadius: 3,
    boxShadow: isDark ? '0 12px 28px rgba(0,0,0,0.34)' : '0 10px 26px rgba(15,23,42,0.08)',
    height: '100%',
    position: 'relative',
    overflow: 'hidden',
    boxSizing: 'border-box',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: 3,
      zIndex: 1,
      background: `linear-gradient(90deg, ${accent} 0%, ${alpha(accent, 0.18)} 100%)`,
    },
  };
}

/** KPI / metric cards — matches Stock chart card pattern. */
export function getDynamicPricingMetricCardSx(isDark, accent = DYNAMIC_PRICING_ACCENT, themeColors) {
  const tokens = themeColors || getDynamicPricingThemeColors(isDark);
  return {
    ...chartCardBase(isDark, tokens, accent, { p: { xs: 2, md: 2.5 } }),
    display: 'flex',
    flexDirection: 'column',
    minHeight: { xs: 120, md: 140 },
  };
}

/** Main chart / form panels. */
export function getDynamicPricingPanelSx(isDark, accent = DYNAMIC_PRICING_ACCENT, options = {}) {
  const themeColors = getDynamicPricingThemeColors(isDark);
  return chartCardBase(isDark, themeColors, accent, options);
}

/** Left guide sidebar — Simulate / Forecast / Config. */
export function getDynamicPricingGuidePanelSx(isDark, accent = DYNAMIC_PRICING_ACCENT) {
  const tokens = getDynamicPricingThemeColors(isDark);
  return {
    p: 3,
    height: '100%',
    borderRadius: 3,
    border: `1px solid ${tokens.surfaceBorder}`,
    overflow: 'hidden',
    position: 'relative',
    boxSizing: 'border-box',
    background: tokens.surfaceGradient,
    boxShadow: isDark ? '0 12px 28px rgba(0,0,0,0.34)' : '0 10px 26px rgba(15,23,42,0.08)',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: 3,
      zIndex: 1,
      background: `linear-gradient(90deg, ${accent} 0%, ${alpha(accent, 0.18)} 100%)`,
    },
  };
}

/** Recommendations sidebar cards. */
export function getDynamicPricingSidebarCardSx(isDark, accent = DYNAMIC_PRICING_ACCENT) {
  const tokens = getDynamicPricingThemeColors(isDark);
  return {
    borderRadius: 3,
    border: `1px solid ${tokens.surfaceBorder}`,
    overflow: 'hidden',
    position: 'relative',
    background: isDark
      ? `linear-gradient(155deg, ${alpha(accent, 0.12)} 0%, ${THEME_COLORS.SURFACE_ELEVATED_DARK} 50%, ${THEME_COLORS.BACKGROUND_DARK} 100%)`
      : `linear-gradient(155deg, ${alpha(accent, 0.1)} 0%, #ffffff 45%, #f8fafc 100%)`,
    boxShadow: isDark ? 'inset 0 1px 0 rgba(255,255,255,0.04)' : 'none',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: 2,
      zIndex: 1,
      background: `linear-gradient(90deg, ${alpha(accent, 0.35)}, ${accent}, ${alpha(accent, 0.35)})`,
    },
  };
}

/** List / datatable shell — matches Stock list papers. */
export function getDynamicPricingListPaperSx(isDark) {
  const themeMode = isDark ? 'dark' : 'light';
  return {
    ...getDashboardPaperSx(themeMode, 'controls'),
    mb: 0,
    p: { xs: 1.5, sm: 2, md: 2.5 },
    overflow: 'hidden',
  };
}

export function getDynamicPricingSectionHeaderTitleSx(isDark) {
  const tokens = getDynamicPricingThemeColors(isDark);
  return {
    fontWeight: 600,
    color: tokens.text,
    mb: 0.5,
  };
}

export function getDynamicPricingSectionHeaderSubtitleSx(isDark) {
  const tokens = getDynamicPricingThemeColors(isDark);
  return {
    color: tokens.subtleText,
  };
}

export function getDynamicPricingDrawerHeaderSx(isDark) {
  const tokens = getDynamicPricingThemeColors(isDark);
  return {
    borderBottom: getDashboardSectionHeaderDividerSx(isDark),
    backgroundColor: tokens.headerBg,
  };
}

export function getDynamicPricingDrawerFooterSx(isDark) {
  const tokens = getDynamicPricingThemeColors(isDark);
  return {
    borderTop: getDashboardSectionHeaderDividerSx(isDark),
    backgroundColor: tokens.headerBg,
  };
}

export function getDynamicPricingPrimaryButtonSx(accent = DYNAMIC_PRICING_ACCENT) {
  return {
    bgcolor: accent,
    color: '#000',
    fontWeight: 600,
    '&:hover': { bgcolor: DYNAMIC_PRICING_ACCENT_HOVER },
  };
}

export function getDynamicPricingOutlinedButtonSx(isDark, accent = DYNAMIC_PRICING_ACCENT) {
  const tokens = getDynamicPricingThemeColors(isDark);
  return {
    borderColor: tokens.surfaceBorder,
    color: isDark ? tokens.text : '#666',
    '&:hover': {
      borderColor: accent,
      backgroundColor: isDark ? alpha(accent, 0.1) : alpha(accent, 0.05),
      color: accent,
    },
  };
}

export function getDynamicPricingSectionOverlineSx(accent = DYNAMIC_PRICING_ACCENT) {
  return {
    color: accent,
    fontWeight: 700,
    letterSpacing: 0.08,
    display: 'block',
  };
}

export function getDynamicPricingGuideAvatarSx(isDark, accent = DYNAMIC_PRICING_ACCENT) {
  return {
    width: 44,
    height: 44,
    bgcolor: isDark ? alpha(accent, 0.2) : alpha(accent, 0.25),
    color: accent,
  };
}

export function getDynamicPricingAccentTabsSx(accent = DYNAMIC_PRICING_ACCENT) {
  return {
    minHeight: 42,
    '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 42 },
    '& .Mui-selected': { color: `${accent} !important` },
    '& .MuiTabs-indicator': { bgcolor: accent, height: 3, borderRadius: '3px 3px 0 0' },
  };
}

export function getDynamicPricingTableContainerSx(isDark, accent = DYNAMIC_PRICING_ACCENT) {
  const tokens = getDynamicPricingThemeColors(isDark);
  return {
    maxHeight: 480,
    overflow: 'auto',
    borderRadius: 3,
    border: `1px solid ${tokens.surfaceBorder}`,
    background: tokens.surfaceGradient,
  };
}

export function getDynamicPricingValueTypographySx(isDark, accent = DYNAMIC_PRICING_ACCENT) {
  return {
    fontWeight: 800,
    color: isDark ? accent : DYNAMIC_PRICING_ACCENT_TEXT_LIGHT,
    letterSpacing: -0.3,
  };
}

export function getDynamicPricingSwitchSx(accent = DYNAMIC_PRICING_ACCENT) {
  return {
    '& .MuiSwitch-switchBase.Mui-checked': { color: accent },
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: accent },
  };
}

export function getDynamicPricingConfigTogglePaperSx(isDark, accent = DYNAMIC_PRICING_ACCENT) {
  const tokens = getDynamicPricingThemeColors(isDark);
  return {
    p: 1.5,
    borderRadius: 2,
    border: `1px solid ${tokens.surfaceBorder}`,
    cursor: 'help',
    background: isDark
      ? `linear-gradient(155deg, ${alpha(accent, 0.08)} 0%, ${THEME_COLORS.SURFACE_ELEVATED_DARK} 100%)`
      : `linear-gradient(155deg, ${alpha(accent, 0.08)} 0%, #fff 100%)`,
  };
}
